import SolutionsGrid from "@/components/site/SolutionsGrid";
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Check, Globe2, ShieldOff, EyeOff, FileText, DollarSign, Bell, MessageSquare, Send, BarChart3, Zap, Users, CheckCircle2, Smartphone, ShieldCheck, Activity, Route, Headset } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Helmet } from "react-helmet-async";
import SiteHeader from "@/components/site/SiteHeader";
import SiteFooter from "@/components/site/SiteFooter";
import CTASection from "@/components/site/CTASection";
import StatsBar from "@/components/site/StatsBar";
import Seo from "@/components/Seo";
import { LikeMark } from "@/components/site/VectorMarks";
import FlatIcon from "@/components/site/FlatIcon";

const challenges = [
  { text: "Low delivery rates on international routes", Icon: Globe2, gradient: "icon-tile-teal" },
  { text: "Messages blocked by carrier filters", Icon: ShieldOff, gradient: "icon-tile-indigo" },
  { text: "Lack of visibility into delivery status", Icon: EyeOff, gradient: "icon-tile-blue" },
  { text: "Complex sender ID registration across countries", Icon: FileText, gradient: "icon-tile-orange" },
  { text: "Unpredictable costs and hidden fees", Icon: DollarSign, gradient: "icon-tile-pink" },
];

const solutions = [
  "Direct carrier connections in 200+ countries",
  "Intelligent routing optimizes for delivery rate and cost",
  "Real-time delivery reports and analytics dashboard",
  "Managed sender ID registration and compliance",
  "Transparent per-message pricing with volume discounts",
];

const benefits = [
  { title: "Reliability", description: "99.99% uptime with redundant infrastructure worldwide.", Icon: ShieldCheck, color: "text-amber" },
  { title: "Monitoring", description: "Real-time delivery tracking and performance analytics.", Icon: Activity, color: "text-sky" },
  { title: "Routing Quality", description: "AI-powered route selection for optimal delivery.", Icon: Route, color: "text-lime" },
  { title: "Support", description: "24/7 technical support with dedicated account managers.", Icon: Headset, color: "text-violet" },
];

const audiences = [
  "E-commerce platforms sending order updates",
  "SaaS companies with user notifications",
  "Marketing teams running SMS campaigns",
  "Enterprises requiring global messaging reach",
];

const faqs = [
  { question: "What is A2P SMS?", answer: "A2P SMS is automated text messaging sent from a business application to mobile users at scale. Common uses include OTP verification, transaction alerts, shipping notifications, and marketing messages." },
  { question: "What is the difference between A2P and P2P SMS?", answer: "P2P is conversational messaging between two people. A2P is automated, high-volume messaging from a business to end users. A2P requires different routing, compliance, and infrastructure." },
  { question: "What is a grey route in A2P SMS?", answer: "A grey route is an unofficial SMS path that bypasses carrier agreements, cheaper but unreliable and increasingly blocked by MNOs. Yootelco uses only direct legal carrier routes." },
  { question: "How is Yootelco different from other messaging platforms?", answer: "Yootelco operates at the wholesale level, built for MNOs, aggregators, and enterprises that need carrier-grade infrastructure and direct network connections — not a self-serve retail platform for small businesses." },
  { question: "What is the A2P SMS market size?", answer: "The global A2P SMS market was valued at over $58 billion in 2026 and is projected to reach $81 billion by 2034, growing at 4.3% annually." },
  { question: "How do I get A2P SMS rates from Yootelco?", answer: "Contact our team via the contact page. We respond within 24 hours with route coverage, pricing, and a commercial proposal for your traffic volumes." },
];

const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map((f) => ({
    "@type": "Question",
    name: f.question,
    acceptedAnswer: { "@type": "Answer", text: f.answer },
  })),
};

const SolutionA2P = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, []);

  return (
    <main className="min-h-screen bg-white">
      <Seo
        title="A2P SMS Messaging for MNOs & Enterprises | Yootelco"
        description="Reliable, carrier-grade A2P SMS for OTPs, alerts, and marketing. Direct connections for MNOs, banks, fintechs, and enterprises. Talk to our team."
        path="/solutions/a2p-sms"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>
      <SiteHeader />

      {/* Hero */}
      <section className="pb-16 pt-[120px] md:pb-20 md:pt-[140px]" style={{ backgroundImage: "var(--gradient-hero)" }}><div className="container">
        <div className="mx-auto max-w-[700px] text-center">
          <h1 className="font-display text-[44px] font-bold leading-[1.05] tracking-tight text-foreground md:text-[60px] [text-wrap:balance]">
            A2P SMS
          </h1>
          <p className="mx-auto mt-[18px] max-w-[700px] text-[15px] font-medium leading-[1.55] text-muted-foreground md:text-[16px] [text-wrap:balance]">
            High-volume application-to-person messaging with global coverage and reliable delivery.
          </p>
          <div className="mt-[28px] flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/contact"
              className="inline-flex rounded-[6px] bg-primary px-[31px] py-[13px] text-[15px] font-bold text-primary-foreground shadow-soft transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lift"
            >
              Contact Sales
            </Link>
          </div>
        </div>
      </div>
      </section>

      {/* How It Works */}
      <section className="section-shell pt-[20px]">
        <div className="mx-auto grid max-w-[1120px] items-center gap-10 lg:grid-cols-[1.15fr_1fr]">
          <div className="flex justify-center">
            <div className="relative h-[440px] w-[500px] max-w-full">
              {/* Floating user tag icons */}
              <span className="absolute left-[80px] top-[10px] z-20 flex h-[68px] w-[68px] items-center justify-center rounded-[18px] bg-gradient-to-br from-sky-400 to-blue-600 text-white shadow-lift" aria-hidden="true"><Users className="h-8 w-8" strokeWidth={2.3} /></span>
              <span className="absolute left-[210px] top-[60px] z-20 flex h-[68px] w-[68px] -rotate-12 items-center justify-center rounded-[18px] bg-gradient-to-br from-emerald-400 to-teal-500 text-white shadow-lift" aria-hidden="true"><MessageSquare className="h-8 w-8" strokeWidth={2.3} /></span>
              <span className="absolute right-[80px] top-[30px] z-20 flex h-[68px] w-[68px] rotate-12 items-center justify-center rounded-[18px] bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lift" aria-hidden="true"><Bell className="h-8 w-8" strokeWidth={2.3} /></span>
              {/* Dashed connection vectors flowing into card */}
              <svg className="absolute left-[92px] top-[78px] z-10 h-[185px] w-[330px] opacity-80" viewBox="0 0 330 185" fill="none" aria-hidden="true">
                <path d="M28 0C38 54 70 94 122 138" stroke="hsl(var(--sky))" strokeWidth="3" strokeDasharray="6 8" strokeLinecap="round" />
                <path d="M156 42C154 82 142 111 120 139" stroke="hsl(var(--lime))" strokeWidth="3" strokeDasharray="6 8" strokeLinecap="round" />
                <path d="M292 20C264 76 210 115 123 139" stroke="hsl(var(--amber))" strokeWidth="3" strokeDasharray="6 8" strokeLinecap="round" />
              </svg>
              {/* Messaging platform card */}
              <div className="absolute bottom-0 left-0 z-10 w-full rounded-[22px] border border-border/60 bg-gradient-to-br from-card to-secondary/30 p-[22px] pl-[34px] shadow-lift">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-[8px]">
                    <span className="flex h-[26px] w-[26px] items-center justify-center rounded-[8px] bg-gradient-to-br from-indigo-500 to-violet-600 shadow-sm">
                      <MessageSquare className="h-[14px] w-[14px]" strokeWidth={2.4} />
                    </span>
                    <span className="text-[14px] font-semibold text-foreground">Messaging platform</span>
                  </div>
                  <div className="relative">
                    <Bell className="h-[18px] w-[18px] text-foreground/70" strokeWidth={2.2} />
                    <span className="absolute -right-1 -top-1 flex h-[12px] min-w-[12px] items-center justify-center rounded-full bg-rose-500 px-[3px] text-[8px] font-bold text-white">3</span>
                  </div>
                </div>
                <div className="mt-[14px] grid grid-cols-2 gap-[10px]">
                  {/* Card 1 — Sent */}
                  <div className="rounded-[12px] border border-border/60 bg-card p-[10px] shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="flex h-[22px] w-[22px] items-center justify-center rounded-[6px] bg-gradient-to-br from-sky-400 to-blue-600">
                        <Send className="h-[11px] w-[11px]" strokeWidth={2.4} />
                      </span>
                      <span className="text-[8px] font-semibold text-emerald-600">+12%</span>
                    </div>
                    <p className="mt-[6px] text-[14px] font-bold leading-none text-foreground">24.8k</p>
                    <p className="mt-[2px] text-[8px] font-medium text-muted-foreground">Sent today</p>
                  </div>
                  {/* Card 2 — Delivered */}
                  <div className="rounded-[12px] border border-border/60 bg-card p-[10px] shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="flex h-[22px] w-[22px] items-center justify-center rounded-[6px] bg-gradient-to-br from-emerald-400 to-green-600">
                        <CheckCircle2 className="h-[11px] w-[11px]" strokeWidth={2.4} />
                      </span>
                      <span className="text-[8px] font-semibold text-emerald-600">99.2%</span>
                    </div>
                    <p className="mt-[6px] text-[14px] font-bold leading-none text-foreground">24.6k</p>
                    <p className="mt-[2px] text-[8px] font-medium text-muted-foreground">Delivered</p>
                  </div>
                </div>
                {/* Big chart card */}
                <div className="mt-[10px] rounded-[12px] border border-border/60 bg-card p-[10px] shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-[6px]">
                      <span className="flex h-[20px] w-[20px] items-center justify-center rounded-[6px] bg-gradient-to-br from-amber-400 to-orange-500">
                        <BarChart3 className="h-[11px] w-[11px]" strokeWidth={2.4} />
                      </span>
                      <span className="text-[9px] font-semibold text-foreground">Delivery rate</span>
                    </div>
                    <div className="flex items-center gap-[4px]">
                      <span className="h-[5px] w-[5px] rounded-full bg-indigo-500" />
                      <span className="text-[7px] font-medium text-muted-foreground">SMS</span>
                      <span className="ml-[4px] h-[5px] w-[5px] rounded-full bg-emerald-500" />
                      <span className="text-[7px] font-medium text-muted-foreground">OTP</span>
                    </div>
                  </div>
                  {/* Bar chart */}
                  <div className="mt-[8px] flex h-[42px] items-end gap-[5px]">
                    {[
                      { a: 60, b: 40 },
                      { a: 75, b: 55 },
                      { a: 50, b: 70 },
                      { a: 85, b: 60 },
                      { a: 70, b: 80 },
                      { a: 90, b: 65 },
                      { a: 80, b: 90 },
                    ].map((bar, i) => (
                      <div key={i} className="flex flex-1 items-end gap-[2px]">
                        <span className="flex-1 rounded-t-[2px] bg-gradient-to-t from-indigo-500 to-violet-500" style={{ height: `${bar.a}%` }} />
                        <span className="flex-1 rounded-t-[2px] bg-gradient-to-t from-emerald-400 to-teal-500" style={{ height: `${bar.b}%` }} />
                      </div>
                    ))}
                  </div>
                </div>
                {/* Side step dots with labels */}
                <div className="absolute left-[10px] top-[60px] flex flex-col gap-[10px]">
                  {[
                    { color: "bg-indigo-500", icon: Users },
                    { color: "bg-sky-500", icon: Send },
                    { color: "bg-emerald-500", icon: CheckCircle2 },
                    { color: "bg-amber-500", icon: BarChart3 },
                    { color: "bg-rose-500", icon: Zap },
                  ].map(({ color, icon: Icon }, i) => (
                    <span key={i} className={`flex h-[14px] w-[14px] items-center justify-center rounded-full ${color} shadow-sm ring-2 ring-white`}>
                      <Icon className="h-[7px] w-[7px]" strokeWidth={2.6} />
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div>
            <h2 className="text-[28px] font-bold leading-[1.2] tracking-tight text-foreground md:text-[32px]">What It Is</h2>
            <p className="mt-[16px] max-w-[480px] text-[14.5px] leading-[1.65] text-muted-foreground">
              A2P (Application-to-Person) SMS enables businesses to send automated text messages to customers at scale. Used for notifications, alerts, marketing campaigns, and authentication codes through APIs or messaging platforms.
            </p>
          </div>
        </div>
      </section>

      {/* Common Challenges */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">Common Challenges</h2>
        </div>
        <div className="mx-auto mt-[32px] flex max-w-[1120px] flex-wrap items-stretch justify-center gap-4">
          {challenges.map((c) => (
            <article
              key={c.text}
              className="flex h-full w-full items-start gap-3 rounded-[14px] border border-border/70 bg-secondary/40 px-[18px] py-[16px] sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)]"
            >
              <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-[10px] ${c.gradient}`}>
                <c.Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <p className="text-[13.5px] font-semibold leading-[1.45] text-foreground">{c.text}</p>
            </article>
          ))}
        </div>
      </section>

      {/* How Yootelco Solves It */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">How Yootelco Solves It</h2>
        </div>
        <SolutionsGrid items={solutions} />
      </section>

      {/* Key Benefits */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="text-center">
          <h2 className="why-title">Key Benefits</h2>
        </div>
        <div className="mx-auto mt-[32px] flex max-w-[1080px] flex-wrap justify-center gap-5">
          {benefits.map((b: any, i) => (
            <article key={b.title} className="w-full sm:w-[calc(50%-0.625rem)] lg:w-[calc(33.333%-0.834rem)] rounded-[16px] border border-border/70 bg-secondary/40 p-[24px] text-center">
              <FlatIcon Icon={b.Icon} color={b.color} className="mx-auto block w-fit" />
              <h3 className="mt-[14px] text-[17px] font-bold text-foreground">{b.title}</h3>
              <p className="mx-auto mt-[8px] max-w-[260px] text-[13px] leading-[1.55] text-muted-foreground">
                {b.underline ? <span className="underline">{b.description}</span> : b.description}
              </p>
            </article>
          ))}
        </div>
      </section>

      {/* Who It's For */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto grid max-w-[1120px] items-start gap-12 lg:grid-cols-2">
          <div>
            <h2 className="text-[28px] font-bold leading-[1.2] tracking-tight text-foreground md:text-[32px]">Who It's For</h2>
            <ul className="mt-[18px] space-y-[14px]">
              {audiences.map((a) => (
                <li key={a} className="flex items-start gap-3 text-[14.5px] leading-[1.55] text-foreground">
                  <span className="mt-[8px] h-[9px] w-[9px] flex-none rounded-full bg-primary" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="flex justify-center">
            <div className="relative h-[420px] w-[230px] rounded-[40px] border-[4px] border-foreground bg-gradient-to-b from-secondary/60 via-card to-card shadow-lift">
              {/* Notch */}
              <div className="mx-auto mt-[12px] h-[18px] w-[76px] rounded-full bg-foreground" />
              {/* Floating globe badge */}
              <span className="absolute -right-5 top-[50px] z-20 flex h-[52px] w-[52px] items-center justify-center rounded-[14px] bg-gradient-to-br from-sky-500 to-blue-700 shadow-lift ring-[5px] ring-white">
                <Globe2 className="h-6 w-6" strokeWidth={2.4} />
              </span>
              {/* Floating bell badge */}
              <span className="absolute -left-5 top-[120px] z-20 flex h-[44px] w-[44px] items-center justify-center rounded-[12px] bg-gradient-to-br from-fuchsia-500 to-violet-600 shadow-lift ring-[5px] ring-white">
                <Bell className="h-5 w-5" strokeWidth={2.4} />
              </span>
              {/* Center hero icon */}
              <div className="mx-auto mt-[60px] flex h-[68px] w-[68px] items-center justify-center rounded-[18px] bg-gradient-to-br from-emerald-400 to-green-600 shadow-lift">
                <MessageSquare className="h-8 w-8" strokeWidth={2.2} />
              </div>
              {/* Two app-style icons row */}
              <div className="mt-[18px] flex items-center justify-center gap-[14px]">
                <span className="flex h-[44px] w-[44px] items-center justify-center rounded-[12px] bg-gradient-to-br from-emerald-500 to-teal-600 shadow-soft">
                  <MessageSquare className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <span className="flex h-[44px] w-[44px] items-center justify-center rounded-[12px] bg-gradient-to-br from-rose-500 to-orange-500 shadow-soft">
                  <Zap className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <span className="flex h-[44px] w-[44px] items-center justify-center rounded-[12px] bg-gradient-to-br from-amber-400 to-orange-500 shadow-soft">
                  <Send className="h-5 w-5" strokeWidth={2.4} />
                </span>
              </div>
              {/* Mini analytics card */}
              <div className="absolute bottom-[70px] left-[16px] right-[16px] rounded-[14px] border border-border/60 bg-card px-[14px] py-[12px] shadow-lift">
                <div className="flex items-center gap-[8px]">
                  <span className="flex h-[22px] w-[22px] items-center justify-center rounded-[6px] bg-gradient-to-br from-indigo-500 to-violet-600">
                    <BarChart3 className="h-3 w-3" strokeWidth={2.6} />
                  </span>
                  <div className="h-[6px] w-[60%] rounded-full bg-muted" />
                </div>
                <div className="mt-[8px] flex items-end gap-[3px]">
                  {[40, 65, 50, 80, 60, 90, 70].map((h, i) => (
                    <span key={i} className="flex-1 rounded-t-[2px] bg-gradient-to-t from-sky-400 to-indigo-500" style={{ height: `${h * 0.22}px` }} />
                  ))}
                </div>
              </div>
              {/* Floating purple like (kept) */}
              <LikeMark className="absolute -right-8 bottom-[60px] z-10 h-[44px] w-[44px] sm:-right-10" />
            </div>
          </div>
        </div>
      </section>

      <StatsBar />

      {/* FAQ */}
      <section className="section-shell pt-[64px] md:pt-[80px]">
        <div className="mx-auto max-w-[900px] text-center">
          <h2 className="section-title">Frequently Asked Questions</h2>
        </div>
        <div className="mx-auto mt-10 max-w-[1120px]">
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((item, index) => (
              <AccordionItem key={item.question} value={`item-${index}`} className="border-b border-border">
                <AccordionTrigger className="py-6 text-left text-lg font-bold hover:no-underline md:text-xl">
                  {item.question}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-12 text-base leading-7 text-muted-foreground">
                  {item.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* CTA */}
            <CTASection
        title={<><span className="text-sky">Ready</span> to get started?</>}
        description="Whether you are an MNO, aggregator, or enterprise, we are straightforward to work with and fast to respond."
        useCase="A2P SMS"
      />

      <SiteFooter />
    </main>
  );
};

export default SolutionA2P;